/**
 * NR Atomic Components JavaScript
 * 统一的原子组件 JavaScript 文件，专注于动画功能
 */

/**
 * 检查类是否已经存在，防止重复定义
 */
function checkClassExists(className) {
  return typeof window !== 'undefined' && window[className];
}

/**
 * 工厂函数：获取或创建 NRAtomicContainer 类
 */
function getNRAtomicContainerClass() {
  if (checkClassExists('NRAtomicContainer')) {
    return window.NRAtomicContainer;
  }

  class NRAtomicContainer {
    constructor() {
      this.animationContainers = new Map();
      this.actionContainers = new Map();
      this.lastScrollY = window.scrollY;
      this.isScrollingDown = true;

      this.init();
    }

    init() {
      this.initAnimationContainers();
      this.initActionContainers();
      this.bindEvents();
    }

    initAnimationContainers() {
      const containers = document.querySelectorAll('.nr-atomic-container--anim');

      containers.forEach(container => {
        const containerId = container.id;
        if (!containerId) return;

        const initial = container.getAttribute('data-anim-initial') || '';
        const active = container.getAttribute('data-anim-active') || '';
        const once = container.getAttribute('data-anim-once') === 'true';
        const threshold = parseFloat(container.getAttribute('data-anim-threshold') || '0.2');
        const rootMargin = container.getAttribute('data-anim-root-margin') || '0px';
        const intersectionRatio = parseFloat(container.getAttribute('data-anim-intersection-ratio') || '0');
        const triggerDirection = container.getAttribute('data-anim-trigger-direction') || 'down';

        if (initial && initial.trim()) {
          container.classList.add('nr-atomic-container--anim-initial');
        }

        if (!('IntersectionObserver' in window)) {
          if (active && active.trim()) {
            container.classList.remove('nr-atomic-container--anim-initial');
            container.classList.add('nr-atomic-container--anim-active');
          }
          return;
        }

        this.animationContainers.set(containerId, {
          element: container,
          initial,
          active,
          once,
          threshold,
          rootMargin,
          intersectionRatio,
          triggerDirection,
          isAnimationActive: false,
          hasTriggeredAnimation: false,
          animationTimeout: null,
          observer: null,
          isTransitioning: false,
          lastStateChange: 0
        });

        this.setupAnimationObserver(containerId);
      });
    }

    initActionContainers() {
      const containers = document.querySelectorAll('.nr-atomic-container--has-actions');

      containers.forEach(container => {
        const containerId = container.id;
        if (!containerId) return;

        const actionsData = container.getAttribute('data-actions');
        if (!actionsData) return;

        try {
          const actions = JSON.parse(actionsData);
          if (actions) {
            // 单独处理 scaleImage，只添加 CSS 类，不需要事件监听
            if (actions.scaleImage) {
              container.classList.add('nr-scale-container');
            }

            // 只有需要事件监听的动作才添加到容器管理
            if (actions.openLink || actions.goAnchor) {
              this.actionContainers.set(containerId, {
                element: container,
                actions: actions
              });

              this.setupActionHandlers(containerId);
            }
          }
        } catch (error) {
          console.warn('Failed to parse actions data for container:', containerId, error);
        }
      });
    }

    setupActionHandlers(containerId) {
      const containerData = this.actionContainers.get(containerId);
      if (!containerData) return;

      const { element, actions } = containerData;

      // 为容器添加点击事件监听器
      element.addEventListener('click', (event) => {
        this.handleActionClick(containerId, event);
      });

      // 添加可点击的视觉提示样式
      if (actions.openLink || actions.goAnchor) {
        element.style.cursor = 'pointer';
        element.setAttribute('tabindex', '0');
        element.setAttribute('role', 'button');

        // 添加键盘事件支持
        element.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.handleActionClick(containerId, event);
          }
        });
      }
    }

    handleActionClick(containerId, event) {
      const containerData = this.actionContainers.get(containerId);
      if (!containerData) return;

      const { actions } = containerData;

      // 处理 openLink 动作
      if (actions.openLink) {
        const { url, target = '_self' } = actions.openLink;
        if (url) {
          window.open(url, target);
        }
      }

      // 处理 goAnchor 动作
      if (actions.goAnchor) {
        const { nodeID } = actions.goAnchor;
        if (nodeID) {
          // 统一处理 nodeID 为数组
          const nodeIDs = Array.isArray(nodeID) ? nodeID : [nodeID];

          // 构建选择器字符串，一次性查询所有元素
          const selector = nodeIDs.map(id => `[data-node-id="${id}"]`).join(', ');
          const elements = document.querySelectorAll(selector);

          // 查找第一个非隐藏的元素
          for (const element of elements) {
            if (!element.hidden && element.offsetParent !== null) {
              element.scrollIntoView({
                behavior: 'smooth'
              });
              break; // 找到第一个可见元素后停止
            }
          }
        }
      }
    }

    setupAnimationObserver(containerId) {
      const containerData = this.animationContainers.get(containerId);
      if (!containerData) return;

      const { element, threshold, rootMargin, intersectionRatio, once } = containerData;

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const isCurrentlyIntersecting = entry.isIntersecting || entry.intersectionRatio >= intersectionRatio;

            if (isCurrentlyIntersecting) {
              this.handleAnimationEnter(containerId);
              if (once) {
                observer.unobserve(element);
              }
            } else {
              this.handleAnimationLeave(containerId);
            }
          }
        },
        { threshold, rootMargin }
      );

      containerData.observer = observer;
      observer.observe(element);
    }

    handleAnimationEnter(containerId) {
      const containerData = this.animationContainers.get(containerId);
      if (!containerData) return;

      const { element, active, isAnimationActive, isTransitioning, lastStateChange } = containerData;
      const now = Date.now();

      // 防止频繁状态切换，增加间隔时间
      if (now - lastStateChange < 500) return;

      if (active && active.trim() && !isAnimationActive && !isTransitioning && this.shouldTriggerAnimation(containerId)) {
        containerData.isTransitioning = true;
        containerData.lastStateChange = now;

        element.classList.add('nr-atomic-container--anim-active');
        containerData.isAnimationActive = true;
        containerData.hasTriggeredAnimation = true;

        // 动画完成后解除锁定
        setTimeout(() => {
          containerData.isTransitioning = false;
        }, 600);
      }
    }

    handleAnimationLeave(containerId) {
      const containerData = this.animationContainers.get(containerId);
      if (!containerData) return;

      const { once, hasTriggeredAnimation, isTransitioning, lastStateChange } = containerData;
      const now = Date.now();

      // 防止频繁状态切换，增加间隔时间
      if (now - lastStateChange < 500) return;

      // 只有当元素完全移出视口时才触发移除动画
      if (!once && hasTriggeredAnimation && !isTransitioning && this.shouldRemoveAnimation(containerId)) {
        // 增加延迟，确保元素确实移出了视口
        this.debounceAnimationChange(containerId, () => {
          this.removeAnimation(containerId);
        }, 300); // 增加延迟时间
      }
    }

    removeAnimation(containerId) {
      const containerData = this.animationContainers.get(containerId);
      if (!containerData) return;

      const { element, initial, isAnimationActive, isTransitioning, lastStateChange } = containerData;
      const now = Date.now();

      // 防止频繁状态切换
      if (now - lastStateChange < 300) return;

      // 简化判断逻辑，主要依赖 IntersectionObserver
      if (!isAnimationActive || isTransitioning) {
        return;
      }

      // 使用更简单的视口检查
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // 只有当元素完全移出视口时才移除动画
      const isCompletelyOutOfViewport = rect.bottom <= 0 || rect.top >= viewportHeight;

      if (isCompletelyOutOfViewport && initial && initial.trim()) {
        containerData.isTransitioning = true;
        containerData.lastStateChange = now;

        element.classList.remove('nr-atomic-container--anim-active');
        containerData.isAnimationActive = false;
        containerData.hasTriggeredAnimation = false;

        // 动画完成后解除锁定
        setTimeout(() => {
          containerData.isTransitioning = false;
        }, 600);
      }
    }

    shouldTriggerAnimation(containerId) {
      const containerData = this.animationContainers.get(containerId);
      if (!containerData) return false;

      const { triggerDirection } = containerData;

      if (triggerDirection === 'both') return true;
      if (triggerDirection === 'up' && !this.isScrollingDown) return true;
      if (triggerDirection === 'down' && this.isScrollingDown) return true;
      return false;
    }

    shouldRemoveAnimation(containerId) {
      const containerData = this.animationContainers.get(containerId);
      if (!containerData) return false;

      const { triggerDirection } = containerData;

      if (triggerDirection === 'both') return true;
      if (triggerDirection === 'up' && this.isScrollingDown) return true;
      if (triggerDirection === 'down' && !this.isScrollingDown) return true;
      return false;
    }

    debounceAnimationChange(containerId, callback, delay = 100) {
      const containerData = this.animationContainers.get(containerId);
      if (!containerData) return;

      if (containerData.animationTimeout) {
        clearTimeout(containerData.animationTimeout);
      }
      containerData.animationTimeout = setTimeout(callback, delay);
    }

    bindEvents() {
      let scrollTimeout;
      let isScrolling = false;
      let scrollRAF = null;

      const handleScroll = () => {
        // 取消之前的 requestAnimationFrame
        if (scrollRAF) {
          cancelAnimationFrame(scrollRAF);
        }

        // 使用 requestAnimationFrame 优化滚动处理
        scrollRAF = requestAnimationFrame(() => {
          this.isScrollingDown = window.scrollY > this.lastScrollY;
          this.lastScrollY = window.scrollY;
          isScrolling = true;

          // 清除之前的超时
          if (scrollTimeout) {
            clearTimeout(scrollTimeout);
          }

          // 滚动结束后延迟检查所有动画状态
          scrollTimeout = setTimeout(() => {
            isScrolling = false;
            // 只在必要时检查动画状态
            if (this.animationContainers.size > 0) {
              this.checkAllAnimationStates();
            }
          }, 300); // 增加延迟时间，减少检查频率
        });
      };

      // 使用 passive 监听器提高性能
      window.addEventListener('scroll', handleScroll, { passive: true });

      // 保存清理函数
      this._cleanupScroll = () => {
        window.removeEventListener('scroll', handleScroll);
        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }
        if (scrollRAF) {
          cancelAnimationFrame(scrollRAF);
        }
      };
    }

    checkAllAnimationStates() {
      // 使用 requestAnimationFrame 优化批量操作
      requestAnimationFrame(() => {
        this.animationContainers.forEach((containerData, containerId) => {
          if (!containerData.isTransitioning) {
            // 只在元素确实需要状态更新时才检查
            const { element, isAnimationActive } = containerData;

            // 使用 IntersectionObserver 的结果而不是重新计算
            if (containerData.observer) {
              // 让 IntersectionObserver 处理，避免重复计算
              return;
            }

            // 如果必须手动检查，使用更高效的方式
            const rect = element.getBoundingClientRect();
            const viewportHeight = window.innerHeight;

            // 只有当元素完全移出视口或重新进入视口时才检查
            const isCompletelyOutOfViewport = rect.bottom <= 0 || rect.top >= viewportHeight;
            const isInViewport = rect.top < viewportHeight && rect.bottom > 0;

            if (isCompletelyOutOfViewport || (isInViewport && !isAnimationActive)) {
              this.removeAnimation(containerId);
            }
          }
        });
      });
    }

    destroy() {
      // 清理所有 IntersectionObserver
      this.animationContainers.forEach((containerData) => {
        if (containerData.observer) {
          containerData.observer.disconnect();
        }
        if (containerData.animationTimeout) {
          clearTimeout(containerData.animationTimeout);
        }
      });

      // 清理 action 容器的事件监听器
      this.actionContainers.forEach((containerData) => {
        const { element } = containerData;
        if (element) {
          // 移除事件监听器（通过克隆元素来移除所有事件监听器）
          const newElement = element.cloneNode(true);
          element.parentNode.replaceChild(newElement, element);
        }
      });

      // 清理滚动事件监听器（如果有的话）
      if (this._cleanupScroll) {
        this._cleanupScroll();
      }

      this.animationContainers.clear();
      this.actionContainers.clear();
    }
  }

  // 将类注册到全局作用域
  if (typeof window !== 'undefined') {
    window.NRAtomicContainer = NRAtomicContainer;
  }

  return NRAtomicContainer;
}

/**
 * 工厂函数：获取或创建 NRAtomicComponents 类
 */
function getNRAtomicComponentsClass() {
  if (checkClassExists('NRAtomicComponents')) {
    return window.NRAtomicComponents;
  }

  const NRAtomicContainer = getNRAtomicContainerClass();

  class NRAtomicComponents {
    constructor() {
      this.container = null;
      this.init();
    }

    init() {
      // 初始化容器组件（仅动画功能）
      this.container = new NRAtomicContainer();
    }

    destroy() {
      if (this.container) {
        this.container.destroy();
      }
    }
  }

  // 将类注册到全局作用域
  if (typeof window !== 'undefined') {
    window.NRAtomicComponents = NRAtomicComponents;
  }

  return NRAtomicComponents;
}

/**
 * 检查是否已经初始化过
 */
function isAlreadyInitialized() {
  return typeof window !== 'undefined' && window.nrAtomicComponents;
}

/**
 * 初始化函数
 */
function initializeComponents() {
  // 防止重复初始化
  if (isAlreadyInitialized()) {
    console.warn('NR Atomic Components 已经初始化过了，跳过重复初始化');
    return window.nrAtomicComponents;
  }

  const NRAtomicComponents = getNRAtomicComponentsClass();
  const instance = new NRAtomicComponents();

  // 注册到全局作用域
  if (typeof window !== 'undefined') {
    window.nrAtomicComponents = instance;
    // 保持向后兼容性
    window.nrAtomicContainer = instance.container;
  }

  return instance;
}

// 在 DOMContentLoaded 时初始化
document.addEventListener('DOMContentLoaded', initializeComponents);

// 如果 DOMContentLoaded 已经触发，立即初始化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeComponents);
} else {
  initializeComponents();
}

// 导出工厂函数（用于模块化）
if (typeof window !== 'undefined') {
  window.getNRAtomicContainerClass = getNRAtomicContainerClass;
  window.getNRAtomicComponentsClass = getNRAtomicComponentsClass;
  window.initializeNRAtomicComponents = initializeComponents;
}
